"use client"

import { zodResolver } from "@hookform/resolvers/zod"
import { useTranslations } from "next-intl"
import { useForm } from "react-hook-form"
import { toast } from "sonner"
import { z } from "zod"

import { signin } from "@/app/[locale]/auth/actions"
import { Heading } from "@/components/Heading"
import {
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  FormSubmitButton,
  FormWrapper,
} from "@/components/shadcn/Form"
import { Input } from "@/components/shadcn/Input"
import { PasswordInput } from "@/components/shadcn/PasswordInput"
import { Spacer } from "@/components/Spacer"
import { Text } from "@/components/Text"
import { urls } from "@/constants/urls"
import { mxpSignInEvent } from "@/services/mixpanel/mixpanel"
import { Link, useRouter } from "@/utils/navigation"

const formSchema = z.object({
  email: z.email(),
  password: z.string(),
})

export function SignInForm() {
  const router = useRouter()

  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: { email: "", password: "" },
  })

  const t = useTranslations("auth")

  async function onSubmit(values: z.infer<typeof formSchema>) {
    const res = await signin({ email: values.email, password: values.password })

    if (res?.data?.error) {
      toast.error(res.data?.error)
      return
    }

    if (res?.serverError || res?.validationErrors) {
      toast.error("Something went wrong.", {
        description: "Please try again later. If the problem persists, please let us know.",
      })

      return
    }

    if (!res?.data) {
      return
    }

    const { user } = res.data

    if (user) {
      mxpSignInEvent(user)
    }

    router.push(urls.app.home)
  }

  return (
    <>
      <Heading order={1} size={2} className="text-center">
        {t("signInHeading")}
      </Heading>

      <Spacer className="h-10" />

      <FormWrapper {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)}>
          <FormField
            name="email"
            control={form.control}
            render={({ field }) => (
              <FormItem>
                <FormLabel>{t("emailLabel")}</FormLabel>

                <FormControl>
                  <Input {...field} type="email" autoComplete="email" placeholder={t("emailPlaceholder")} />
                </FormControl>

                <FormMessage />
              </FormItem>
            )}
          />

          <Spacer className="h-4" />

          <FormField
            name="password"
            control={form.control}
            render={({ field }) => (
              <FormItem>
                <FormLabel>
                  <Text size="sm">{t("signInPasswordLabel")}</Text>
                </FormLabel>

                <FormControl>
                  <PasswordInput {...field} placeholder="password" autoComplete="current-password" />
                </FormControl>

                <FormMessage />
              </FormItem>
            )}
          />

          <Spacer className="h-10" />

          <FormSubmitButton className="w-full" size="sm">
            {t("signInSubmitButton")}
          </FormSubmitButton>
        </form>
      </FormWrapper>

      <Spacer className="h-4" />

      <div className="flex justify-center gap-4">
        <Link href={urls.auth.signup}>
          <Text variant="link" size="sm">
            {t("signInFirstLogin")}
          </Text>
        </Link>

        <Link href={urls.auth.resetPassword}>
          <Text variant="link" size="sm">
            {t("signInForgotPasswordLink")}
          </Text>
        </Link>
      </div>
    </>
  )
}
